// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import translate from "@insite/client-framework/Translate";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
// import React from "react"; // Store Custom Code
import { useDispatch } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
// import StyledWrapper from "@insite/client-framework/Common/StyledWrapper"; // Store Custom Code
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Button from "@store/MobiusOverrides/Button/Button";
import useSession from "@store/Framework/hooks/useSession";
import validatePassword from "@store/Framework/Store/Store/Context/ShowHideToggle/Handlers/ValidatePassword";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useState } from "react";
/* Store Custom Code - End */

interface OwnProps {
    extendedStyles?: ShowPricingToggleStyles;
    // Store Custom Code
    showPricingPasswordValidation?: boolean;
}

export interface ShowPricingToggleStyles {
    toggleClickable?: ClickablePresentationProps;
    toggleIcon?: IconPresentationProps;
    toggleTypography?: TypographyPresentationProps;
    // Starter Custom Code
    wrapper?: InjectableCss;
    checkbox?: CheckboxProps;
    // Store Custom Code
    passwordModal?: ModalPresentationProps;
    passwordTextField?: TextFieldProps;
    validateButton?: ButtonPresentationProps;
}

export const showPricingToggleStyles: ShowPricingToggleStyles = {
    checkbox: {
        variant: "toggle",
        labelPosition: "right",
        sizeVariant: "small",
    },
    // Store Custom Code
    passwordModal: { sizeVariant: "small" },
    passwordTextField: {
        cssOverrides: {
            formField: css`
                margin: ${pxToRem(12)} 0;
            `,
        },
    },
};

const StyledForm = getStyledWrapper("form"); // Store Custom Code
// Starter Removed - StyledHr

export const ShowPricingToggle = ({
    extendedStyles,
    // Store Custom Code
    showPricingPasswordValidation,
}: OwnProps) => {
    const dispatch = useDispatch();
    const showPricing = useAppSelector(state => state.context.showPricing);
    const styles = useMergeStyles("showPricingToggleStyles", showPricingToggleStyles, extendedStyles);

    /* Store Custom Code - Start */
    const session = useSession();

    const [passwordPromptModalIsOpen, setPasswordPromptModalIsOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(/* externalError || */ "");
    const [password, setPassword] = useState("");
    const [isValidatePasswordClicked, setIsSignInClicked] = useState(false);
    /* Store Custom Code - End */

    const clickShowPricingToggleHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        /* Store Custom Code - Start */
        if (showPricing || !showPricingPasswordValidation) {
            dispatch(setShowPricing({ showPricing: !showPricing }));
        } else {
            setPasswordPromptModalIsOpen(true);
        }
        /* Store Custom Code - End */
    };

    /* Store Custom Code - Start */
    const validatePasswordHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsSignInClicked(true);
        if (!password) {
            return;
        }

        dispatch(
            validatePassword({
                password,
                userName: session.userName,

                onSuccess() {
                    dispatch(setShowPricing({ showPricing: !showPricing }));
                    setPasswordPromptModalIsOpen(false);
                    setIsSignInClicked(false);
                    setPassword("");
                },
                onError: error => {
                    setErrorMessage(error);
                    setIsSignInClicked(false);
                },
            }),
        );
    };

    const passwordErrorMessage =
        !password && isValidatePasswordClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    const onPasswordPromptModalClose = () => {
        setPasswordPromptModalIsOpen(false);
        setErrorMessage("");
        setPassword("");
    };
    /* Store Custom Code - End */

    return (
        <StyledWrapper {...styles.wrapper} className="show-pricing-toggle" /* Starter Custom Code */>
            {/* Starter Custom Code - Start */}
            <Checkbox
                {...styles.checkbox}
                checked={showPricing}
                onChange={clickShowPricingToggleHandler}
                data-test-selector={showPricing ? "NotShowPricing" : "ShowPricing"}
            >
                {translate("Show Pricing")}
            </Checkbox>
            {/* Starter Custom Code - End */}
            {/* Store Custom Code - Start */}
            <Modal
                {...styles.passwordModal}
                headline={translate("Show Pricing")}
                isOpen={passwordPromptModalIsOpen}
                handleClose={onPasswordPromptModalClose}
            >
                <StyledForm {...styles.form} onSubmit={validatePasswordHandler}>
                    <Typography>{translate("Enter your password to show pricing.")}</Typography>
                    <TextField
                        id="password"
                        {...styles.passwordTextField}
                        label={translate("Password")}
                        onChange={e => setPassword(e.currentTarget.value)}
                        value={password}
                        error={passwordErrorMessage}
                        type="password"
                        required // Starter Custom Code
                    />
                    <Button {...styles.validateButton} type="submit">
                        {translate("Validate")}
                    </Button>
                </StyledForm>
            </Modal>
            {/* Store Custom Code - End */}
        </StyledWrapper>
    );
};

export default ShowPricingToggle;
